// Note: FocusOutsideEvent and PointerDownOutsideEvent are not exported from @radix-ui/react-dismissable-layer
// Using any type for now to avoid TypeScript errors
import clsx from 'clsx'
import React, { useRef, useState } from 'react'

import Popover, {
  PopoverAnchor,
  PopoverContent
} from '@/components-latest/primitives/Popover'
import Text from '@/components-latest/primitives/Text'
import { formatLengthValue } from '@/utils/css'

import Styles from './Suggestions.module.scss'

interface ISuggestionsRootProps {
  children: React.ReactElement[]
  trigger: React.ReactElement
  onSelect?: (value: string | number | null) => void
  disabled?: boolean
  maxHeight?: string
}
const Suggestions: React.FC<ISuggestionsRootProps> = ({
  children,
  trigger,
  onSelect,
  maxHeight = 200
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLInputElement | null>(null)

  const handleFocus = () => {
    setIsOpen(true)
  }

  const closePopover = (
    event:
      | KeyboardEvent
      | MouseEvent
      | any // PointerDownOutsideEvent
      | any // FocusOutsideEvent
  ) => {
    if (event.target === triggerRef?.current) return event.preventDefault()
    setIsOpen(false)
  }

  const handleOptionClick = (value: string | number | null) => {
    onSelect?.(value)
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen}>
      <PopoverAnchor asChild>
        <div onFocus={handleFocus} className={Styles.inputWrapper}>
          {React.cloneElement(trigger, {
            onFocus: () => {
              handleFocus()
              trigger?.props?.onFocus?.()
            },
            ref: triggerRef
          })}
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="bottom"
        onOpenAutoFocus={event => event.preventDefault()}
        onEscapeKeyDown={closePopover}
        onPointerDownOutside={closePopover}
        onFocusOutside={closePopover}
        onInteractOutside={closePopover}
        className={Styles.content}
        style={
          (maxHeight
            ? {
                '--max-height': formatLengthValue(maxHeight)
              }
            : {}) as React.CSSProperties
        }>
        {React.Children.map(children, (child, index) => {
          if (child.props.onClick) {
            return React.cloneElement(child, {
              index,
              onClick: () => {
                handleOptionClick(child.props.value)
                child.props.onClick()
              }
            })
          } else {
            return React.cloneElement(child, {
              index,
              onClick: handleOptionClick
            })
          }
        })}
      </PopoverContent>
    </Popover>
  )
}

interface ISuggestionsOptionProps {
  children: React.ReactNode
  index?: number
  value: string | number | null
  onClick?: (value: string | number | null) => void
  disabled?: boolean
  className?: string
}
const SuggestionsOption: React.FC<ISuggestionsOptionProps> = ({
  className,
  children,
  index,
  value,
  onClick,
  disabled
}) => {
  return (
    <button
      className={clsx(Styles.option, className)}
      onClick={() => onClick?.(value)}
      disabled={disabled}>
      <Text variant="nextSubtitle">{children}</Text>
    </button>
  )
}

export default Suggestions
export { SuggestionsOption }
